import React, { useState, useRef, useCallback, useEffect } from "react";
import copy from "copy-to-clipboard";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import { isMobile } from "react-device-detect";
import BasicMenu from './menu';

export default function ParenthesisCounter() {
  const [paragraph, setParagraph] = useState(
    ''
  );

  // Function to extract unique parenthesis pairs
  const extractParenthesisPairs = (text) => {
    const matches = text.match(/{{.*?}}/g); // Capture text inside {{ }}
    return Array.from(new Set(matches ? matches.map((match) => match.slice(2, -2)) : []));
  };

  // Store extracted unique placeholders
  const uniquePairs = extractParenthesisPairs(paragraph);
  const [userInputs, setUserInputs] = useState(
    Object.fromEntries(uniquePairs.map((pair) => [pair, ""]))
  );

  const updateParagraph = useCallback((newMessage) => {
    setParagraph(newMessage);
    const newPairs = extractParenthesisPairs(newMessage);
    setUserInputs((prevInputs) =>
      Object.fromEntries(newPairs.map((pair) => [pair, prevInputs[pair] ?? ""]))
    ); // Preserve previous inputs if possible
  }, []);

  // Handle user input change
  const handleInputChange = (key, value) => {
    setUserInputs((prevInputs) => ({
      ...prevInputs,
      [key]: value, // Update only the specific key
    }));
  };

  // Generate the modified statement
  const generateModifiedStatement = () => {
    let modifiedText = paragraph;
    Object.keys(userInputs).forEach((key) => {
      const value = userInputs[key].trim() || `{{${key}}}`; // Use user input or fallback to original placeholder
      modifiedText = modifiedText.replace(new RegExp(`{{${key}}}`, "g"), value);
    });
    return modifiedText;
  };

   // Reference for the text editor
   const textEditorRef = useRef(null);

   // Focus on the text editor when the component mounts
   useEffect(() => {
     if (textEditorRef.current && !isMobile) {
       textEditorRef.current.focus();
     }
   }, []);

  const inputRefs = useRef({});

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const getFirstEmptyKey = () => {
    return Object.keys(userInputs).find((key) => userInputs[key] === "");
  };

  const getSnackbarMessage = () => {
    let firstKey = getFirstEmptyKey();
    return `The following field has been left blank: ${firstKey}. Please fill in before copying`;
  };

  const focusFirstEmptyInput = () => {
    const emptyKey = getFirstEmptyKey();
    if (emptyKey && inputRefs.current[emptyKey]) {
      inputRefs.current[emptyKey].focus();
    }
  };

  const [copied, setCopied] = useState(false);
  // Copy modified text to clipboard
  const handleCopy = () => {
    if (getFirstEmptyKey()) {
      if (!isMobile){
        focusFirstEmptyInput();
      }
      setSnackbarOpen(true); // Show Snackbar alert
      return;
    }
      copy(generateModifiedStatement());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
  };

  const handleMouseOver = () => {
    if (!isMobile){
      focusFirstEmptyInput();
    }
  };

  // Handle key press to allow Tab insertion in textarea
  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault(); // Prevent default tab behavior
      const { selectionStart, selectionEnd } = e.target;
      const newValue =
        paragraph.substring(0, selectionStart) + "\t" + paragraph.substring(selectionEnd);
      setParagraph(newValue);

      // Move cursor after inserted tab
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = selectionStart + 1;
      }, 0);
    }
  };

  return (
    <div className="p-4 max-w-lg mx-auto bg-white rounded shadow">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold mb-2">Text Editor</h2>
        <div className="flex justify-end mb-2">
        <BasicMenu updateParagraph={updateParagraph}/>
        </div>
      </div>
      {/* Paragraph Input */}
      <TextareaAutosize
        value={paragraph}
        ref={textEditorRef} // Attach ref here
        onChange={(e) => {
          setParagraph(e.target.value);
          const newPairs = extractParenthesisPairs(e.target.value);
          setUserInputs((prevInputs) =>
            Object.fromEntries(newPairs.map((pair) => [pair, prevInputs[pair] ?? ""]))
          ); // Preserve existing values
        }}
        onKeyDown={handleKeyDown} // Enable tab insertion
        className="w-full p-2 border rounded mb-4"
        minRows={3}
        placeholder={`Enter your message here using double curly brackets to add customizable fields:

    Hello {{name}}, thanks for trying {{product}}!`}
      />

      {/* Generate Unique Input Fields */}
      <div className="space-y-2">
        {Object.keys(userInputs).map((key, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[key] = el)}
            type="text"
            placeholder={key} // Placeholder is just the extracted key
            value={userInputs[key]} // Keep user input values
            onChange={(e) => handleInputChange(key, e.target.value)}
            className="w-full p-2 border rounded"
          />
        ))}
      </div>
      {paragraph.trim() && (
        <div id="parent_result"  onMouseOver={handleMouseOver}>
          {/* Result Box */}
          <div className="flex items-center justify-between">
            <h3 className="mt-4 text-xl font-bold mb-2">Result</h3>
            <div className="flex justify-end">
              {/* Copy Button */}
              <button
                onClick={handleCopy}
                className="mt-2 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>
          <div className="mt-2 p-4 border rounded bg-gray-100">
          <div
            className="whitespace-pre-wrap font-sans"
            dangerouslySetInnerHTML={{ __html: generateModifiedStatement() }}
          ></div>
          </div>
        </div>
      )}
       {/* Snackbar Alert */}
       <Snackbar 
            anchorOrigin={{"vertical":"top", "horizontal": "center"}} 
            open={snackbarOpen} 
            autoHideDuration={3000} 
            onClose={handleSnackbarClose}
            key = "topcenterSnackbar">
        <Alert onClose={handleSnackbarClose} severity="error" variant="filled" sx={{ width: "100%" }}>
          {getSnackbarMessage()}
        </Alert>
      </Snackbar>
    </div>
  );
}
